import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, Terminal, Database, Server, Cpu, Shield, Zap, LayoutDashboard, FileText, CheckCircle2, Code2, Workflow, Rocket } from 'lucide-react';
// Custom hook for scroll spy
const useScrollSpy = (ids: string[], offset: number = 100) => {
  const [activeId, setActiveId] = useState('');
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: `-${offset}px 0px -80% 0px`
    });
    ids.forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [ids, offset]);
  return activeId;
};
const SECTIONS = [{
  id: 'welcome',
  title: 'Welcome & Opportunity',
  icon: Rocket
}, {
  id: 'overview',
  title: 'Product Overview',
  icon: Target
}, {
  id: 'prototype',
  title: 'Current Prototype',
  icon: LayoutDashboard
}, {
  id: 'architecture',
  title: 'Technical Architecture',
  icon: Server
}, {
  id: 'roadmap',
  title: 'Feature Roadmap',
  icon: Workflow
}, {
  id: 'schema',
  title: 'Database Schema',
  icon: Database
}, {
  id: 'ai',
  title: 'AI Integration Details',
  icon: Cpu
}, {
  id: 'api',
  title: 'API Design',
  icon: Terminal
}, {
  id: 'deployment',
  title: 'Deployment & DevOps',
  icon: Zap
}, {
  id: 'workflow',
  title: 'Development Workflow',
  icon: Code2
}, {
  id: 'metrics',
  title: 'Success Metrics',
  icon: Target
}, {
  id: 'getting-started',
  title: 'Getting Started',
  icon: CheckCircle2
}];
// Reusable components for the document
const SectionHeader = ({
  num,
  title
}: {
  num: string;
  title: string;
}) => <div className="flex items-baseline gap-4 mb-8 border-b border-stone-800 pb-4" data-id="element-1709">
    <span className="font-serif text-3xl text-red-600" data-id="element-1710">{num}</span>
    <h2 className="font-serif text-3xl text-stone-50" data-id="element-1711">{title}</h2>
  </div>;
const Code = ({
  children
}: {
  children: React.ReactNode;
}) => <code className="font-mono text-sm bg-stone-900 px-1.5 py-0.5 text-stone-300 rounded-sm" data-id="element-1712">
    {children}
  </code>;
const Callout = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="border-l-4 border-red-600 bg-stone-900/50 p-6 my-6 text-stone-300 leading-relaxed" data-id="element-1713">
    {children}
  </div>;
const Card = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-1714">
    <h4 className="font-medium text-stone-50 mb-3" data-id="element-1715">{title}</h4>
    <div className="text-stone-400 text-sm leading-relaxed space-y-2" data-id="element-1716">
      {children}
    </div>
  </div>;
export function DevPlan() {
  const activeId = useScrollSpy(SECTIONS.map(s => s.id));
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-stone-950 text-stone-300 font-sans selection:bg-red-600/30" data-id="element-1717">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-stone-950/80 backdrop-blur-md border-b border-stone-800 z-50 flex items-center px-6" data-id="element-1718">
        <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-xs uppercase tracking-widest" data-id="element-1719">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1720" />
          Back to site
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 flex items-start gap-16" data-id="element-1721">
        {/* Left Sidebar (TOC) */}
        <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto custom-scrollbar pr-4" data-id="element-1722">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1723">
            Table of Contents
          </p>
          <nav className="space-y-1" data-id="element-1724">
            {SECTIONS.map((section, idx) => {
            const isActive = activeId === section.id;
            const Icon = section.icon;
            return <button key={section.id} onClick={() => scrollTo(section.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors rounded-sm ${isActive ? 'bg-stone-900 text-stone-50' : 'text-stone-500 hover:text-stone-300 hover:bg-stone-900/50'}`} data-id="element-1725">
                  <span className={`font-mono text-[10px] ${isActive ? 'text-red-500' : 'text-stone-600'}`} data-id="element-1726">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="truncate" data-id="element-1727">{section.title}</span>
                </button>;
          })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl" data-id="element-1728">
          {/* Header */}
          <header className="mb-20" data-id="element-1729">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-950/30 border border-red-900/50 text-red-500 font-mono text-xs uppercase tracking-widest mb-8" data-id="element-1730">
              <Shield className="w-3.5 h-3.5" data-id="element-1731" />
              Confidential — For Development Team Only
            </div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-stone-50 leading-[1.1] mb-6" data-id="element-1732">
              BlockQuote.ai — Product Requirements Document & Development Plan
            </h1>
            <p className="text-xl text-stone-400 leading-relaxed" data-id="element-1733">
              Everything you need to build, deploy, and scale the infrastructure
              layer for group travel.
            </p>
          </header>

          <div className="space-y-24" data-id="element-1734">
            {/* Section 1 */}
            <section id="welcome" className="scroll-mt-24" data-id="element-1735">
              <SectionHeader num="01" title="Welcome to BlockQuote.ai" data-id="element-1736" />
              <div className="space-y-6 text-lg" data-id="element-1737">
                <p data-id="element-1738">
                  Welcome to the team. This is a greenfield SaaS platform for
                  the group travel industry.
                </p>
                <p data-id="element-1739">
                  BlockQuote.ai replaces the broken email-based quoting process
                  for resort group business (weddings, corporate retreats,
                  incentive trips) with instant, AI-powered quoting
                  infrastructure.
                </p>
                <p data-id="element-1740">
                  The prototype and marketing site are already built (what
                  you're looking at). Your job is to take this from prototype to
                  production.
                </p>
                <p data-id="element-1741">
                  <strong data-id="element-1742">Tech philosophy:</strong> We leverage AI-assisted
                  development (Claude Code, Cursor, etc.) for speed. We deploy
                  on Vercel. We move fast but build properly.
                </p>
                <Callout data-id="element-1743">
                  You will be working directly with the founder. This is a
                  high-impact, high-autonomy role.
                </Callout>
              </div>
            </section>

            {/* Section 2 */}
            <section id="overview" className="scroll-mt-24" data-id="element-1744">
              <SectionHeader num="02" title="What BlockQuote.ai Does" data-id="element-1745" />
              <ul className="space-y-4 list-disc list-inside ml-4 text-lg" data-id="element-1746">
                <li data-id="element-1747">
                  <strong data-id="element-1748">Core value prop:</strong> Instant group quotes,
                  one-click holds, automated contracts, direct resort pricing.
                </li>
                <li data-id="element-1749">
                  <strong data-id="element-1750">Two user types:</strong> Resort Groups (supply side)
                  and Travel Agencies (demand side).
                </li>
                <li data-id="element-1751">
                  The platform eliminates wholesale leakage (15-25% margin lost
                  to intermediaries) by enabling direct distribution.
                </li>
                <li data-id="element-1752">
                  <strong data-id="element-1753">Business model:</strong> 5% of verified net savings
                  (savings-share). We only earn when the resort saves money.
                </li>
                <li data-id="element-1754">
                  <strong data-id="element-1755">Key proof point:</strong> Moon Palace / Palace Resorts
                  proved this model internally. DestaWed became their #1 agency
                  using instant quoting. BlockQuote packages this for every
                  resort.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="prototype" className="scroll-mt-24" data-id="element-1756">
              <SectionHeader num="03" title="What's Already Built (Prototype)" data-id="element-1757" />
              <p className="mb-6 text-lg" data-id="element-1758">
                The following pages and components are already built in the
                current repository:
              </p>

              <h3 className="font-serif text-2xl text-stone-50 mb-4 mt-8" data-id="element-1759">
                Pages
              </h3>
              <div className="space-y-3" data-id="element-1760">
                <p data-id="element-1761">
                  <Code data-id="element-1762">/</Code> (LandingPage) — Full marketing site with Hero,
                  Problem, Solution, Case Study, Features, For Resorts, For
                  Agencies, Agency Signup, Business Model, AI Capabilities,
                  Roadmap, CTA sections
                </p>
                <p data-id="element-1763">
                  <Code data-id="element-1764">/deck</Code> (PitchDeck) — Investor pitch deck,
                  slide-based presentation with 15+ slides
                </p>
                <p data-id="element-1765">
                  <Code data-id="element-1766">/pilot_for_Lomas</Code> (ResortSalesDeck) —
                  Resort-focused sales deck, 11 slides with financial models
                </p>
                <p data-id="element-1767">
                  <Code data-id="element-1768">/app</Code> (Dashboard) — Resort dashboard prototype
                  with Overview, Quotes, Holds, Analytics tabs, quote detail
                  view
                </p>
                <p data-id="element-1769">
                  <Code data-id="element-1770">/agency</Code> (AgencyPortal) — Agency portal with
                  property browser, booking builder, my bookings views
                </p>
                <p data-id="element-1771">
                  <Code data-id="element-1772">/onboarding/lomas</Code> (LomasOnboarding) — 8-step
                  client onboarding wizard for Lomas Group pilot
                </p>
                <p data-id="element-1773">
                  <Code data-id="element-1774">/dev-plan</Code> (this page)
                </p>
              </div>

              <h3 className="font-serif text-2xl text-stone-50 mb-4 mt-8" data-id="element-1775">
                Component Inventory
              </h3>
              <div className="space-y-3" data-id="element-1776">
                <p data-id="element-1777">
                  <strong data-id="element-1778">Landing:</strong> Navbar, Hero, Problem, Solution,
                  CaseStudy, Features, ForResorts, ForAgencies, AgencySignup,
                  BusinessModel, AICapabilities, Roadmap, CTASection, Footer
                </p>
                <p data-id="element-1779">
                  <strong data-id="element-1780">Dashboard:</strong> Sidebar, OverviewTab, QuotesTab,
                  QuoteDetail, HoldsTab, AnalyticsTab
                </p>
                <p data-id="element-1781">
                  <strong data-id="element-1782">Agency:</strong> AgencyNav, PropertyBrowser,
                  BookingBuilder, MyBookings
                </p>
              </div>

              <Callout data-id="element-1783">
                <strong data-id="element-1784">Note:</strong> All current components are frontend-only
                with mock data. The production build requires a real backend,
                database, authentication, and AI integration.
              </Callout>
            </section>

            {/* Section 4 */}
            <section id="architecture" className="scroll-mt-24" data-id="element-1785">
              <SectionHeader num="04" title="Production Tech Stack" data-id="element-1786" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1787">
                <Card title="Frontend" data-id="element-1788">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1789">
                    <li data-id="element-1790">React 18+ with TypeScript</li>
                    <li data-id="element-1791">Tailwind CSS for styling</li>
                    <li data-id="element-1792">Framer Motion for animations</li>
                    <li data-id="element-1793">React Router for navigation</li>
                    <li data-id="element-1794">
                      Deployment: Vercel (with preview deployments per PR)
                    </li>
                  </ul>
                </Card>
                <Card title="Backend" data-id="element-1795">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1796">
                    <li data-id="element-1797">
                      Next.js API Routes OR separate Node.js/Express API
                      (developer's recommendation welcome)
                    </li>
                    <li data-id="element-1798">
                      PostgreSQL (via Supabase or Neon) for primary database
                    </li>
                    <li data-id="element-1799">
                      Redis for caching (quote locks, rate limiting, session
                      management)
                    </li>
                    <li data-id="element-1800">Prisma ORM for database access</li>
                  </ul>
                </Card>
                <Card title="Authentication & Authorization" data-id="element-1801">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1802">
                    <li data-id="element-1803">Clerk or NextAuth for authentication</li>
                    <li data-id="element-1804">
                      Role-based access: Resort Admin, Resort Staff, Agency
                      Admin, Agency Agent, Super Admin
                    </li>
                    <li data-id="element-1805">
                      Multi-tenant architecture: each resort group is an
                      isolated tenant
                    </li>
                  </ul>
                </Card>
                <Card title="AI & Intelligence Layer" data-id="element-1806">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1807">
                    <li data-id="element-1808">
                      Anthropic Claude API (claude-sonnet) for: RFP
                      parsing/intake automation, negotiation copilot
                      suggestions, document Q&A via RAG
                    </li>
                    <li data-id="element-1809">
                      RAG Pipeline: LangChain or LlamaIndex for document
                      ingestion, Pinecone or Supabase pgvector for vector
                      storage, PDF/Doc parsing via Unstructured or LlamaParse
                    </li>
                    <li data-id="element-1810">
                      RateBrain (pricing intelligence): Competitive rate
                      monitoring, demand pattern analysis, dynamic pricing
                      recommendations (Phase 2+)
                    </li>
                  </ul>
                </Card>
                <Card title="Infrastructure" data-id="element-1811">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1812">
                    <li data-id="element-1813">Vercel for frontend + serverless functions</li>
                    <li data-id="element-1814">
                      Supabase for database + auth + storage + vector search (or
                      equivalent)
                    </li>
                    <li data-id="element-1815">Resend or SendGrid for transactional email</li>
                    <li data-id="element-1816">Stripe for payment processing (future)</li>
                    <li data-id="element-1817">Upstash Redis for caching and rate limiting</li>
                  </ul>
                </Card>
                <Card title="Development Tools" data-id="element-1818">
                  <ul className="list-disc list-inside space-y-1" data-id="element-1819">
                    <li data-id="element-1820">Claude Code / Cursor for AI-assisted development</li>
                    <li data-id="element-1821">GitHub for version control</li>
                    <li data-id="element-1822">Linear or GitHub Issues for project management</li>
                    <li data-id="element-1823">Vitest for testing</li>
                  </ul>
                </Card>
              </div>
            </section>

            {/* Section 5 */}
            <section id="roadmap" className="scroll-mt-24" data-id="element-1824">
              <SectionHeader num="05" title="Feature Roadmap — What to Build" data-id="element-1825" />

              <div className="space-y-8" data-id="element-1826">
                <div data-id="element-1827">
                  <h3 className="font-serif text-2xl text-stone-50 mb-4 flex items-center gap-3" data-id="element-1828">
                    <span className="font-mono text-sm bg-stone-800 px-2 py-1 rounded-sm" data-id="element-1829">
                      Phase 1
                    </span>
                    MVP (Weeks 1-6)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 ml-2" data-id="element-1830">
                    <li data-id="element-1831">
                      Authentication & multi-tenant setup (resort + agency
                      accounts)
                    </li>
                    <li data-id="element-1832">
                      Resort onboarding flow (property setup, rate sheet upload,
                      policy configuration)
                    </li>
                    <li data-id="element-1833">
                      Quote engine — instant quote generation from agency
                      requests (rooms, dates, events → Good/Better/Best
                      packages)
                    </li>
                    <li data-id="element-1834">
                      Hold system — one-click inventory holds with auto-expiry
                      timers
                    </li>
                    <li data-id="element-1835">
                      Agency portal — property browsing, quote requests, hold
                      management
                    </li>
                    <li data-id="element-1836">
                      Resort dashboard — quote management, hold tracking, basic
                      analytics
                    </li>
                    <li data-id="element-1837">
                      Email notifications (quote sent, hold expiring, deposit
                      due)
                    </li>
                    <li data-id="element-1838">
                      Proposal PDF generation (branded, professional proposals)
                    </li>
                  </ol>
                </div>

                <div data-id="element-1839">
                  <h3 className="font-serif text-2xl text-stone-50 mb-4 flex items-center gap-3" data-id="element-1840">
                    <span className="font-mono text-sm bg-stone-800 px-2 py-1 rounded-sm" data-id="element-1841">
                      Phase 2
                    </span>
                    Intelligence (Weeks 7-12)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 ml-2" data-id="element-1842">
                    <li data-id="element-1843">
                      AI intake automation — parse email RFPs into structured
                      quote requests using Claude
                    </li>
                    <li data-id="element-1844">
                      RAG pipeline — ingest resort SOPs, policies, rate sheets
                      for AI-powered Q&A
                    </li>
                    <li data-id="element-1845">
                      Pricing guardrails — rate floor enforcement, concession
                      limit checks, blackout date validation
                    </li>
                    <li data-id="element-1846">
                      RateBrain v1 — competitive set monitoring, basic demand
                      signals
                    </li>
                    <li data-id="element-1847">
                      Analytics dashboard — conversion rates, quote velocity,
                      leakage tracking
                    </li>
                    <li data-id="element-1848">
                      Contract workflow — digital contracts with e-signature
                      integration
                    </li>
                  </ol>
                </div>

                <div data-id="element-1849">
                  <h3 className="font-serif text-2xl text-stone-50 mb-4 flex items-center gap-3" data-id="element-1850">
                    <span className="font-mono text-sm bg-stone-800 px-2 py-1 rounded-sm" data-id="element-1851">
                      Phase 3
                    </span>
                    Scale (Weeks 13-20)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 ml-2" data-id="element-1852">
                    <li data-id="element-1853">
                      RateBrain v2 — dynamic pricing recommendations, win
                      probability scoring
                    </li>
                    <li data-id="element-1854">Multi-property portfolio management</li>
                    <li data-id="element-1855">
                      Agency network effects — cross-resort search and
                      comparison
                    </li>
                    <li data-id="element-1856">Negotiation copilot — AI-suggested counter-offers</li>
                    <li data-id="element-1857">
                      Advanced reporting — financial impact, savings
                      verification, ROI dashboards
                    </li>
                    <li data-id="element-1858">
                      API for PMS/CRM integrations (Opera, Salesforce, etc.)
                    </li>
                  </ol>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section id="schema" className="scroll-mt-24" data-id="element-1859">
              <SectionHeader num="06" title="Data Model Overview" data-id="element-1860" />
              <p className="mb-6 text-lg" data-id="element-1861">
                Key entities for the database schema:
              </p>
              <ul className="space-y-3 list-disc list-inside ml-4" data-id="element-1862">
                <li data-id="element-1863">
                  <Code data-id="element-1864">Organization</Code> (resort group or agency, tenant
                  isolation)
                </li>
                <li data-id="element-1865">
                  <Code data-id="element-1866">Property</Code> (belongs to org, has rooms, rates,
                  policies)
                </li>
                <li data-id="element-1867">
                  <Code data-id="element-1868">User</Code> (belongs to org, has role)
                </li>
                <li data-id="element-1869">
                  <Code data-id="element-1870">RateSheet</Code> (belongs to property, seasonal rates,
                  room types, blackouts)
                </li>
                <li data-id="element-1871">
                  <Code data-id="element-1872">PolicyConfig</Code> (rate floors, concession limits,
                  approval thresholds)
                </li>
                <li data-id="element-1873">
                  <Code data-id="element-1874">Quote</Code> (the core entity — agency request → resort
                  response)
                </li>
                <li data-id="element-1875">
                  <Code data-id="element-1876">QuotePackage</Code> (Good/Better/Best options within a
                  quote)
                </li>
                <li data-id="element-1877">
                  <Code data-id="element-1878">Hold</Code> (inventory reservation with expiry)
                </li>
                <li data-id="element-1879">
                  <Code data-id="element-1880">Contract</Code> (generated from accepted quote)
                </li>
                <li data-id="element-1881">
                  <Code data-id="element-1882">Document</Code> (uploaded files for RAG — SOPs,
                  policies, templates)
                </li>
                <li data-id="element-1883">
                  <Code data-id="element-1884">AuditLog</Code> (every action tracked)
                </li>
              </ul>
            </section>

            {/* Section 7 */}
            <section id="ai" className="scroll-mt-24" data-id="element-1885">
              <SectionHeader num="07" title="AI Architecture & Implementation" data-id="element-1886" />

              <div className="space-y-8" data-id="element-1887">
                <Card title="RFP Parsing (Phase 1)" data-id="element-1888">
                  <p data-id="element-1889">
                    <strong data-id="element-1890">Input:</strong> Raw email text or form submission
                    from agency
                  </p>
                  <p data-id="element-1891">
                    <strong data-id="element-1892">Process:</strong> Claude extracts structured fields
                    (dates, room count, event types, budget signals, special
                    requests)
                  </p>
                  <p data-id="element-1893">
                    <strong data-id="element-1894">Output:</strong> Structured QuoteRequest object
                    ready for the quote engine
                  </p>
                  <p data-id="element-1895">
                    <strong data-id="element-1896">Prompt engineering:</strong> Few-shot examples from
                    real RFPs, structured output format
                  </p>
                </Card>

                <Card title="RAG Pipeline (Phase 2)" data-id="element-1897">
                  <p data-id="element-1898">
                    <strong data-id="element-1899">Document ingestion:</strong> Upload resort SOPs,
                    policies, rate logic → chunk → embed → store in vector DB
                  </p>
                  <p data-id="element-1900">
                    <strong data-id="element-1901">Query flow:</strong> User question → embed →
                    retrieve relevant chunks → Claude generates answer with
                    citations
                  </p>
                  <p data-id="element-1902">
                    <strong data-id="element-1903">Use cases:</strong> "What's our attrition policy for
                    groups over 50 rooms?", "What concessions can I offer for a
                    December wedding?"
                  </p>
                  <p className="text-amber-500 mt-2" data-id="element-1904">
                    <strong data-id="element-1905">Safety:</strong> AI never invents rates. All pricing
                    comes from rate sheets and policy configs.
                  </p>
                </Card>

                <Card title="RateBrain Intelligence (Phase 2-3)" data-id="element-1906">
                  <p data-id="element-1907">
                    <strong data-id="element-1908">Competitive monitoring:</strong> Track competitor
                    rates via rate shopping APIs
                  </p>
                  <p data-id="element-1909">
                    <strong data-id="element-1910">Demand signals:</strong> Historical booking
                    patterns, seasonality, lead time analysis
                  </p>
                  <p data-id="element-1911">
                    <strong data-id="element-1912">Recommendations:</strong> Suggested rate
                    adjustments, optimal package configurations
                  </p>
                  <p data-id="element-1913">
                    <strong data-id="element-1914">Constraint:</strong> All recommendations are bounded
                    by resort policy — AI suggests, rules decide
                  </p>
                </Card>
              </div>
            </section>

            {/* Section 8 */}
            <section id="api" className="scroll-mt-24" data-id="element-1915">
              <SectionHeader num="08" title="Key API Endpoints" data-id="element-1916" />
              <div className="space-y-4 font-mono text-sm" data-id="element-1917">
                <div data-id="element-1918">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1919">
                    Auth
                  </p>
                  <p data-id="element-1920">
                    <Code data-id="element-1921">POST /api/auth/signup</Code>,{' '}
                    <Code data-id="element-1922">POST /api/auth/login</Code>,{' '}
                    <Code data-id="element-1923">GET /api/auth/me</Code>
                  </p>
                </div>
                <div data-id="element-1924">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1925">
                    Properties
                  </p>
                  <p data-id="element-1926">
                    <Code data-id="element-1927">GET /api/properties</Code>,{' '}
                    <Code data-id="element-1928">POST /api/properties</Code>,{' '}
                    <Code data-id="element-1929">GET /api/properties/:id</Code>
                  </p>
                </div>
                <div data-id="element-1930">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1931">
                    Rate Sheets
                  </p>
                  <p data-id="element-1932">
                    <Code data-id="element-1933">POST /api/properties/:id/rates</Code>,{' '}
                    <Code data-id="element-1934">PUT /api/rates/:id</Code>
                  </p>
                </div>
                <div data-id="element-1935">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1936">
                    Quotes
                  </p>
                  <p data-id="element-1937">
                    <Code data-id="element-1938">POST /api/quotes</Code> (create from agency request),{' '}
                    <Code data-id="element-1939">GET /api/quotes</Code>,{' '}
                    <Code data-id="element-1940">GET /api/quotes/:id</Code>,{' '}
                    <Code data-id="element-1941">PUT /api/quotes/:id/respond</Code> (resort responds
                    with packages), <Code data-id="element-1942">POST /api/quotes/:id/hold</Code>{' '}
                    (agency places hold)
                  </p>
                </div>
                <div data-id="element-1943">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1944">
                    Holds
                  </p>
                  <p data-id="element-1945">
                    <Code data-id="element-1946">GET /api/holds</Code>,{' '}
                    <Code data-id="element-1947">DELETE /api/holds/:id</Code> (release),{' '}
                    <Code data-id="element-1948">POST /api/holds/:id/convert</Code> (to contract)
                  </p>
                </div>
                <div data-id="element-1949">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1950">
                    Documents
                  </p>
                  <p data-id="element-1951">
                    <Code data-id="element-1952">POST /api/documents/upload</Code> (for RAG),{' '}
                    <Code data-id="element-1953">GET /api/documents</Code>
                  </p>
                </div>
                <div data-id="element-1954">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1955">
                    AI
                  </p>
                  <p data-id="element-1956">
                    <Code data-id="element-1957">POST /api/ai/parse-rfp</Code>,{' '}
                    <Code data-id="element-1958">POST /api/ai/ask</Code> (RAG query),{' '}
                    <Code data-id="element-1959">POST /api/ai/rate-suggestion</Code>
                  </p>
                </div>
                <div data-id="element-1960">
                  <p className="text-stone-500 mb-1 uppercase tracking-widest text-xs" data-id="element-1961">
                    Analytics
                  </p>
                  <p data-id="element-1962">
                    <Code data-id="element-1963">GET /api/analytics/overview</Code>,{' '}
                    <Code data-id="element-1964">GET /api/analytics/leakage</Code>
                  </p>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section id="deployment" className="scroll-mt-24" data-id="element-1965">
              <SectionHeader num="09" title="Deployment Strategy" data-id="element-1966" />
              <ul className="space-y-3 list-disc list-inside ml-4 text-lg" data-id="element-1967">
                <li data-id="element-1968">
                  <strong data-id="element-1969">Primary:</strong> Vercel (frontend + API routes)
                </li>
                <li data-id="element-1970">
                  <strong data-id="element-1971">Database:</strong> Supabase (PostgreSQL + pgvector +
                  Auth + Storage)
                </li>
                <li data-id="element-1972">
                  <strong data-id="element-1973">CI/CD:</strong> GitHub → Vercel auto-deploy (preview
                  on PR, production on main)
                </li>
                <li data-id="element-1974">
                  <strong data-id="element-1975">Environments:</strong> Development (local), Staging
                  (Vercel preview), Production (Vercel)
                </li>
                <li data-id="element-1976">
                  <strong data-id="element-1977">Monitoring:</strong> Vercel Analytics + Sentry for
                  error tracking
                </li>
                <li data-id="element-1978">
                  <strong data-id="element-1979">Environment variables:</strong> Database URL,
                  Anthropic API key, Clerk keys, Resend API key, etc.
                </li>
              </ul>
            </section>

            {/* Section 10 */}
            <section id="workflow" className="scroll-mt-24" data-id="element-1980">
              <SectionHeader num="10" title="Development Workflow" data-id="element-1981" />
              <ul className="space-y-3 list-disc list-inside ml-4 text-lg" data-id="element-1982">
                <li data-id="element-1983">
                  <strong data-id="element-1984">AI-assisted development:</strong> Use Claude Code and
                  Cursor extensively. Write prompts, review output, iterate.
                </li>
                <li data-id="element-1985">
                  <strong data-id="element-1986">Branch strategy:</strong> <Code data-id="element-1987">main</Code>{' '}
                  (production), <Code data-id="element-1988">develop</Code> (staging),{' '}
                  <Code data-id="element-1989">feature/*</Code> branches
                </li>
                <li data-id="element-1990">
                  <strong data-id="element-1991">Code review:</strong> All PRs reviewed before merge.
                  AI-generated code gets extra scrutiny.
                </li>
                <li data-id="element-1992">
                  <strong data-id="element-1993">Communication:</strong> Daily async updates, weekly
                  sync calls
                </li>
                <li data-id="element-1994">
                  <strong data-id="element-1995">Documentation:</strong> Keep README updated, document
                  API endpoints, comment complex logic
                </li>
                <li data-id="element-1996">
                  <strong data-id="element-1997">Testing:</strong> Unit tests for business logic,
                  integration tests for API, E2E for critical flows
                </li>
              </ul>
            </section>

            {/* Section 11 */}
            <section id="metrics" className="scroll-mt-24" data-id="element-1998">
              <SectionHeader num="11" title="How We Measure Success" data-id="element-1999" />
              <ul className="space-y-3 list-disc list-inside ml-4 text-lg" data-id="element-2000">
                <li data-id="element-2001">
                  <strong data-id="element-2002">Quote velocity:</strong> Time from agency request to
                  quote delivery (target: under 60 seconds)
                </li>
                <li data-id="element-2003">
                  <strong data-id="element-2004">Conversion rate:</strong> Quotes → Holds → Contracts
                  (target: 2x improvement over email)
                </li>
                <li data-id="element-2005">
                  <strong data-id="element-2006">Leakage reduction:</strong> % of group revenue shifted
                  from wholesale to direct
                </li>
                <li data-id="element-2007">
                  <strong data-id="element-2008">Agency adoption:</strong> Number of active agencies
                  using the platform
                </li>
                <li data-id="element-2009">
                  <strong data-id="element-2010">System reliability:</strong> 99.9% uptime, sub-200ms
                  API response times
                </li>
              </ul>
            </section>

            {/* Section 12 */}
            <section id="getting-started" className="scroll-mt-24" data-id="element-2011">
              <SectionHeader num="12" title="Your First Week" data-id="element-2012" />
              <ol className="list-decimal list-inside space-y-3 ml-4 text-lg mb-12" data-id="element-2013">
                <li data-id="element-2014">Review this PRD thoroughly</li>
                <li data-id="element-2015">
                  Explore the prototype (all pages linked from the landing site)
                </li>
                <li data-id="element-2016">
                  Set up local development environment (clone repo, install
                  deps, run dev server)
                </li>
                <li data-id="element-2017">Set up Vercel project and connect GitHub repo</li>
                <li data-id="element-2018">Provision Supabase project (database + auth + storage)</li>
                <li data-id="element-2019">Get Anthropic API key for Claude integration</li>
                <li data-id="element-2020">Implement authentication (Clerk or Supabase Auth)</li>
                <li data-id="element-2021">Create database schema (Prisma migrations)</li>
                <li data-id="element-2022">
                  Build the first real API endpoint:{' '}
                  <Code data-id="element-2023">POST /api/quotes</Code>
                </li>
                <li data-id="element-2024">Deploy to staging and verify</li>
              </ol>

              <Callout data-id="element-2025">
                <strong data-id="element-2026">
                  Questions? Ideas? Disagreements with any of the above?
                </strong>{' '}
                Good. We want a developer who thinks critically and brings their
                own expertise. This PRD is a starting point, not a straitjacket.
                Let's build something great.
              </Callout>
            </section>
          </div>

          <footer className="mt-32 pt-8 border-t border-stone-800 text-center" data-id="element-2027">
            <p className="font-mono text-xs text-stone-600" data-id="element-2028">
              Built with Magic Patterns
            </p>
          </footer>
        </main>
      </div>
    </div>;
}